import { useRef, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@/lib/cx';
import styles from './Dialog.module.css';
import { useFocusTrap } from './useFocusTrap';
import { useLayer } from './useLayer';

/** Чем закрыт диалог: Esc, «Назад» или клик по подложке — для телеметрии закрытий. */
export type DialogCloseReason = 'escape' | 'back' | 'backdrop';

export interface DialogProps {
  open: boolean;
  onClose: (reason?: DialogCloseReason) => void;
  children: ReactNode;
  /** id заголовка внутри диалога. */
  labelledBy?: string;
  ariaLabel?: string;
  /** Ширина панели: 400 (confirm) · 420 (help, picker) · 640 (paywall). */
  maxWidth?: number;
  /** На мобильном — нижний шит вместо центрированной карточки. */
  sheetOnMobile?: boolean;
  /** Слой над другими диалогами (paywall поверх picker). */
  elevated?: boolean;
  className?: string;
  /** Отдельный слой истории/Esc уже управляется снаружи. */
  manageLayer?: boolean;
}

/**
 * Модальный диалог: role="dialog", ловушка фокуса, Esc и «Назад» закрывают
 * (NAV-10), скролл страницы под ним заблокирован со счётчиком (NFR-7).
 */
export function Dialog({
  open,
  onClose,
  children,
  labelledBy,
  ariaLabel,
  maxWidth = 420,
  sheetOnMobile = false,
  elevated = false,
  className,
  manageLayer = true,
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  useLayer(open && manageLayer, onClose, { lockScroll: true });
  useFocusTrap(panelRef, open);

  if (!open) return null;

  const onOverlayClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose('backdrop');
  };

  return createPortal(
    <div
      className={cx(
        styles.overlay,
        sheetOnMobile && styles.sheetOverlay,
        elevated && styles.elevated,
      )}
      onMouseDown={onOverlayClick}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={ariaLabel}
        tabIndex={-1}
        className={cx(styles.panel, sheetOnMobile && styles.sheet, className)}
        style={{ '--dialog-max': `${maxWidth}px` } as CSSProperties}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
